import { useVariant } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon, ArrowRightIcon } from '@calendly/ui/components/icons';
import { Modal } from '@calendly/ui/components/modal';
import { Sheet } from '@calendly/ui/components/sheet';
import { HeadlineText, BodyText, TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useMemo, useEffect, useRef, useCallback, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common';
import { useBillingPath } from 'features/billing/hooks';
import { IncreaseQuantityModal } from 'features/billing/modals/change_quantity_modal';
import { AddUsersModal } from 'features/invite_flow';
import { fetchStatistics } from 'features/organization_statistics';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrack, useTrackOnce } from 'hooks/analytics_events/useTrack';
import NotifyAddTemmatesImage from './assets/image.svg';
const headerCss = "h1ig8ckw";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const closeButtonCss = "c1d8330s";
const CloseButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(closeButtonCss, props.className)} />);
const wizardCss = "wtay4i8";
const Wizard = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(wizardCss, props.className)} />);
const wizardPhoneCss = "w1s40mno";
const WizardPhone = forwardRef((props, ref) => <FixedContainer ref={ref} {...props} className={classNames(wizardPhoneCss, props.className)} />);
const imageContainerCss = "i1jh69h1";
const ImageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(imageContainerCss, props.className)} />;
});
const imgCss = "i19xy5ou";
const Img = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  return <Component ref={ref} {...restProps} className={classNames(imgCss, props.className)} />;
});
const bodyContainerIsPhoneCss = "b8vuojk";
const bodyContainerCss = "bddt6pa";
const BodyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(bodyContainerCss, {
    [bodyContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const subTextContainerCss = "s10uh32z";
const SubTextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subTextContainerCss, props.className)} />;
});
const featureListIsPhoneCss = "f1vkskrp";
const featureListCss = "fxdttj2";
const FeatureList = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(featureListCss, {
    [featureListIsPhoneCss]: isPhone
  }, props.className)} />;
});
const featureListTextIsPhoneCss = "f1ckw56i";
const featureListTextCss = "f1w8o2j0";
const FeatureListText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(featureListTextCss, {
    [featureListTextIsPhoneCss]: isPhone
  }, props.className)} />;
});
const arrowRightContainerIsPhoneCss = "a1lvaj2e";
const arrowRightContainerCss = "a1im3biu";
const ArrowRightContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(arrowRightContainerCss, {
    [arrowRightContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const featureListContainerIsPhoneCss = "f10elows";
const featureListContainerCss = "f130tdah";
const FeatureListContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(featureListContainerCss, {
    [featureListContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const ButtonContainerCss = "bd4d49c";
const ButtonStyleContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(ButtonContainerCss, props.className)} />;
});
const ModalHeader = ({
  onClose
}) => {
  const isPhone = useDeviceType('isPhone');
  if (isPhone) {
    return <div>
        <HeadlineText size="small" level="3">{I18n.t('publisher.notify_add_teammates_modal.title')}</HeadlineText>
        <TitleText variant="normal">
          {`${I18n.t('publisher.notify_add_teammates_modal.description')}:`}
        </TitleText>
      </div>;
  }
  return <>
      <Header>
        <HeadlineText size="small" level="3">{I18n.t('publisher.notify_add_teammates_modal.title')}</HeadlineText>
        <CloseButton onClick={onClose}>
          <CloseIcon size="large" />
        </CloseButton>
      </Header>
      <SubTextContainer>
        <BodyText size="large">{`${I18n.t('publisher.notify_add_teammates_modal.description')}:`}</BodyText>
      </SubTextContainer>
    </>;
};
const FeatureListItem = ({
  feature,
  subnote
}) => <FeatureList>
    <ArrowRightContainer>
      <ArrowRightIcon color="#006BFF" />
    </ArrowRightContainer>
    <FeatureListText>
      <BodyText weight="bold" size="medium" level="3">{feature}</BodyText>
      <BodyText size="medium" level="3">{subnote}</BodyText>
    </FeatureListText>
  </FeatureList>;
const FeatureListContent = () => <FeatureListContainer>
      <FeatureListItem feature={I18n.t('publisher.notify_add_teammates_modal.features.round_robin.title')} subnote={I18n.t('publisher.notify_add_teammates_modal.features.round_robin.description')} />
      <FeatureListItem feature={I18n.t('publisher.notify_add_teammates_modal.features.groups.title')} subnote={I18n.t('publisher.notify_add_teammates_modal.features.groups.description')} />
      <FeatureListItem feature={I18n.t('publisher.notify_add_teammates_modal.features.booking_on_behalf.title')} subnote={I18n.t('publisher.notify_add_teammates_modal.features.booking_on_behalf.description')} />
    </FeatureListContainer>;
const ButtonContainer = ({
  onAddTeammates,
  onClose
}) => {
  const isPhone = useDeviceType('isPhone');
  const CancelButton = isPhone ? <Button size="large" decoration="secondary-outline" wide onClick={onClose}>
      {I18n.t('publisher.notify_add_teammates_modal.cancel_button')}
    </Button> : null;
  return <ButtonStyleContainer>
      <Button size="large" decoration="primary" wide onClick={onAddTeammates}>
        {I18n.t('publisher.notify_add_teammates_modal.cta_button')}
      </Button>
      {CancelButton}
    </ButtonStyleContainer>;
};
export const NotifyAddTemmatesModal = ({
  availableSeats,
  experimentName
}) => {
  // false to prevent experiment exposure
  const experimentVariant = useVariant(experimentName, false) || '';
  const [update] = useUpdateUserActivationDataMutation();
  const [selectedValue, setSelectedValue] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const [hasTrackedOpen, setHasTrackedOpen] = useState(false);
  const wizardBodyRef = useRef(null); // Used to focus the modal content instead of close button
  const updateRef = useRef(update);
  const isPhone = useDeviceType('isPhone');
  const billingPath = useBillingPath();
  const track = useTrack();
  const {
    data: {
      activation_data: {
        seen_notify_add_teammates_modal_dates: seenNotifyAddTemmatesModalDates
      }
    } = {}
  } = useGetUserQuery();
  const modalDatesOpened = useMemo(() => seenNotifyAddTemmatesModalDates || [], [seenNotifyAddTemmatesModalDates]);
  const modalDatesRef = useRef(modalDatesOpened);
  const modalName = useMemo(() => experimentName === 'growth_add_seats_after_sub_standard' ? 'add_seats_after_subs_intro_standard' : 'add_seats_after_subs_intro_team', [experimentName]);
  const location = useMemo(() => 'auto_load', []);
  const platformType = useMemo(() => isPhone ? 'mobile_app' : 'web_app', [isPhone]);

  // Keep refs updated
  useEffect(() => {
    updateRef.current = update;
    modalDatesRef.current = modalDatesOpened;
  }, [update, modalDatesOpened]);
  const lastSeenNotifyAddTemmatesModalDate = modalDatesOpened.length > 0 ? modalDatesOpened[modalDatesOpened.length - 1] : null;
  const isVariantEligible = useCallback(() => {
    // If they've never seen the modal, they're eligible
    if (!lastSeenNotifyAddTemmatesModalDate) {
      return true;
    }
    // we only want to show day 0, 7+, and 14+
    if (modalDatesOpened.length >= 3) {
      return false;
    }

    // Get the last time they saw the modal from the array
    const lastSeenDate = new Date(lastSeenNotifyAddTemmatesModalDate);
    const now = new Date();
    const daysSinceLastSeen = Math.floor((now - lastSeenDate) / (1000 * 60 * 60 * 24));

    // Show modal if it's been at least 7 days since they last saw it
    return daysSinceLastSeen >= 7;
  }, [modalDatesOpened, lastSeenNotifyAddTemmatesModalDate]);
  const trackOnceConfig = useMemo(() => ({
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      action: 'opening',
      modal_name: modalName,
      location,
      platform_type: platformType
    }
  }), [modalName, location, platformType]);
  useTrackOnce(hasTrackedOpen, trackOnceConfig);
  useEffect(() => {
    if (!hasTrackedOpen && selectedValue === 'NotifyAddTemmates') {
      const seenModalDates = [...(modalDatesRef.current || []), new Date().toISOString()];
      updateRef.current({
        seen_notify_add_teammates_modal_dates: seenModalDates
      });
      setHasTrackedOpen(true);
    }
  }, [selectedValue, hasTrackedOpen]);
  useEffect(() => {
    // Focus the wizard body to prevent close button from being auto-focused
    if (wizardBodyRef.current) {
      wizardBodyRef.current.focus();
    }
  }, []);
  useEffect(() => {
    // Set initial selectedValue if eligible
    if (experimentVariant.startsWith('variant') && isVariantEligible()) {
      setSelectedValue('NotifyAddTemmates');
    }
  }, [experimentVariant, isVariantEligible]);
  const onClose = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'close',
        location,
        modal_name: modalName,
        platform_type: platformType
      }
    });
    setSelectedValue(null);
  }, [track, location, platformType, modalName]);
  const onAddTeammates = useCallback(() => {
    if (availableSeats > 0) {
      setSelectedValue('AddUsers');
    } else {
      setSelectedValue('IncreaseQuantity');
    }
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      properties: {
        cta_destination: 'billing_page',
        cta_key: modalName,
        cta_text: 'Add teammates',
        platform_type: platformType
      }
    });
    history.push(billingPath);
  }, [availableSeats, track, modalName, platformType, history, billingPath]);
  const handleCreditCardChangeTap = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'change_card',
        location,
        modal_name: modalName,
        platform_type: platformType
      }
    });
  }, [track, modalName, platformType, location]);
  const handleIncreaseQuantityClose = useCallback(() => {
    setSelectedValue(null);
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'cancel',
        location,
        modal_name: modalName,
        platform_type: platformType,
        modal_details: 'add_seats'
      }
    });
  }, [track, modalName, platformType, location]);
  const handleIncreaseQuantitySubmit = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      properties: {
        cta_key: modalName,
        cta_text: 'Add seats',
        platform_type: platformType
      }
    });
  }, [track, modalName, platformType]);
  const handleIncreaseQuantitySuccess = useCallback(() => {
    dispatch(fetchStatistics());
    setSelectedValue('AddUsers');
  }, [dispatch]);
  const handleAddUsersClose = useCallback(() => {
    setSelectedValue(null);
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'cancel',
        location,
        modal_name: modalName,
        platform_type: platformType,
        modal_details: 'invite_team_members'
      }
    });
  }, [track, modalName, platformType, location]);
  const handleAddUsersInvite = useCallback(() => {
    setSelectedValue(null);
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'invite teammates success',
        modal_name: modalName,
        location,
        platform_type: platformType,
        modal_details: 'invite_team_members'
      }
    });
  }, [track, modalName, platformType, location]);
  return <>
      {selectedValue === 'NotifyAddTemmates' && (() => {
      const WizardBody = isPhone ? WizardPhone : Wizard;
      const ImageDiv = !isPhone ? <ImageContainer>
              <Img src={NotifyAddTemmatesImage} />
            </ImageContainer> : null;
      const Body = <WizardBody ref={wizardBodyRef} tabIndex={-1}>
            {ImageDiv}
            <BodyContainer>
              <ModalHeader onClose={onClose} />
              <FeatureListContent />
              <ButtonContainer onAddTeammates={onAddTeammates} onClose={onClose} />
            </BodyContainer>
          </WizardBody>;
      return isPhone ? Body : <Modal isOpen onClose={onClose}>
            {Body}
          </Modal>;
    })()}
      {selectedValue === 'IncreaseQuantity' && <IncreaseQuantityModal onExpand={handleCreditCardChangeTap} onClose={handleIncreaseQuantityClose} onSubmit={handleIncreaseQuantitySubmit} onSuccess={handleIncreaseQuantitySuccess} defaultQuantity="1" />}
      {selectedValue === 'AddUsers' && <AddUsersModal isOpen onClose={handleAddUsersClose} onSuccess={handleAddUsersInvite} />}
    </>;
};

require("./notify_add_teammates_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notify_add_teammates_modal.js");